/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo, useEffect } from "react";
import {
  Granularity as APIGranularity,
  useApplicationsCostPerServiceQuery,
} from "~/generated/graphql";
import {
  mapGranularity,
  ChartType,
  Granularity,
  TableData,
  generateApplicationGroupData,
} from "~/components";
import { useQueryState } from "~/hooks";
import { isNotEmpty, isNotNil } from "~/tools";
import { getCategoriesList } from "~/views/applications/ApplicationCostTabs/hooks/utils";
import { generateDataWithCorrections } from "./helpers";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  showOtherServicesCost: boolean;
  handleOtherCostsSwitch: (enable: boolean) => void;
};

type Pagination = {
  page: number;
  limit: number;
};
export function useApplicationsCostPerServices({
  startDate,
  endDate,
  granularity,
  showOtherServicesCost,
  handleOtherCostsSwitch,
}: Props) {
  const { state } = useQueryState<Pagination>();

  const { data, loading, error } = useApplicationsCostPerServiceQuery({
    variables: {
      limit: 10,
      page: state.page,
      startDate,
      endDate,
      granularity: mapGranularity(granularity),
      tableGranularity: APIGranularity.Monthly,
    },
  });

  useEffect(() => {
    if (!loading) {
      const pages = Number(data?.customerServicesCost.pages ?? 0);
      handleOtherCostsSwitch(pages > 1);
    }
  }, [data, loading, handleOtherCostsSwitch]);

  const costPerServicesTableTimePoints = useMemo(
    () => (data?.customerServicesTableCost.timePoints ?? []).filter(isNotNil),
    [data]
  );

  const costPerServicesTableData: TableData[] = useMemo(
    () =>
      generateApplicationGroupData(
        costPerServicesTableTimePoints ?? [],
        showOtherServicesCost,
        ChartType.COST_PER_SERVICES
      ),
    [costPerServicesTableTimePoints, showOtherServicesCost]
  );

  const costPerServicesChartData = useMemo(
    () =>
      generateDataWithCorrections(
        (data?.customerServicesCost.timePoints ?? []).filter(isNotNil),
        (data?.customerCorrectionCost.timePoints ?? []).filter(isNotNil)
      ),
    [data]
  );

  const costPerServicesTotal = Number(data?.customerServicesTableCost ?? "0");

  const services = getCategoriesList(costPerServicesTableData);

  const isCorrections = useMemo(
    () =>
      isNotEmpty(
        (data?.customerCorrectionCost.timePoints ?? []).filter(isNotNil)
      ),
    [data]
  );

  return {
    costPerServicesChartData,
    costPerServicesTableData,
    costPerServicesTotal,
    loading,
    error,
    count: data?.customerServicesCost.count,
    services,
    isCorrections,
  };
}
